import { Checkbox } from '@latitude/checkbox';
import { cn } from '@latitude/token-utils';
import type { MouseEvent, Ref } from 'react';
import { useCallback, useId, useMemo } from 'react';

import { CardListItem } from './CardListItem';
import type { CardListItemCheckboxProps, CardListItemProps } from './types';

/**
 * CardListItemCheckbox combines a CardListItem with a Checkbox in the leading slot.
 * This is a form-agnostic component that can be used with any form library.
 */
const CardListItemCheckbox = ({
  checked,
  onCheckedChange,
  disabled,
  invalid,
  onBlur,
  checkboxTestId,
  className,
  title,
  subtitle,
  trailing,
  size,
  state,
  variant,
  'data-testid': dataTestId,
  ref,
}: CardListItemCheckboxProps & {
  ref?: Ref<HTMLButtonElement>;
}) => {
  const labelId = useId();

  // Memoize the card state calculation based on checkbox state and other conditions
  const cardState = useMemo((): CardListItemProps['state'] => {
    if (disabled) {
      return 'disabled';
    } else if (invalid) {
      return 'error';
    } else if (checked) {
      return 'selected';
    }
    return state || 'enabled';
  }, [disabled, invalid, checked, state]);

  const handleCardClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      // Early return if disabled
      if (disabled) {
        return;
      }

      // Use nativeEvent to get the actual DOM target
      const nativeTarget = event.nativeEvent.target as HTMLElement;

      // Don't toggle if clicking directly on or within a button/input
      if (
        nativeTarget.tagName === 'BUTTON' ||
        nativeTarget.tagName === 'INPUT' ||
        nativeTarget.closest('button') ||
        nativeTarget.closest('input')
      ) {
        return;
      }

      // If we reached here, it's a safe click on the card area (like title/subtitle)
      if (onCheckedChange) {
        onCheckedChange(!checked);
        event.stopPropagation();
      }
    },
    [disabled, checked, onCheckedChange],
  );

  return (
    <div
      onClick={handleCardClick}
      tabIndex={-1}
      aria-disabled={disabled}
      aria-checked={checked ? 'true' : 'false'}
      className={cn('cursor-pointer', { 'cursor-not-allowed': disabled })}
    >
      <CardListItem
        data-testid={dataTestId}
        className={className}
        leading={
          <Checkbox
            className="pt-1"
            ref={ref}
            disabled={disabled}
            invalid={invalid}
            onBlur={onBlur}
            checked={checked}
            onCheckedChange={onCheckedChange}
            data-testid={checkboxTestId}
            rawProps={
              title || subtitle ? { 'aria-labelledby': labelId } : undefined
            }
            disableWrapperClick
          />
        }
        title={title}
        subtitle={subtitle}
        trailing={trailing}
        labelId={labelId}
        size={size}
        state={cardState}
        variant={variant}
      />
    </div>
  );
};

CardListItemCheckbox.displayName = 'CardListItemCheckbox';

export { CardListItemCheckbox };
